import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { shouldBeNumber } from '@platform/helpers/number.helper';
import { isNil, noop } from 'lodash';
import { takeUntil } from 'rxjs/operators';

import { SelectOption } from '@platform/types';
import { EMMAFormElementComponent } from './emma-form-element.component';
import {
  EMMATextAreaFooterDirective,
  EMMATextAreaHeaderDirective,
  EMMATextAreaHighlightDirective,
} from './emma-textarea.directive';

const VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMATextAreaComponent),
  multi: true,
};

const VALIDATORS = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMATextAreaComponent),
  multi: true,
};

export enum AUTOCOMPLETE_CHECK_TYPE {
  AUDIENCE_TAGS = 'audience_tags',
}

@Component({
    selector: 'emma-textarea',
    templateUrl: './emma-textarea.component.html',
    styleUrls: ['./emma-textarea.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        VALIDATORS,
        VALUE_ACCESSOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMATextAreaComponent,
        },
    ],
    standalone: false
})
export class EMMATextAreaComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor, Validator
{
  @ViewChild('textarea', { static: true }) textareaEl!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('highlight', { static: true }) highlightEl!: ElementRef<HTMLDivElement>;
  // Custom templates
  /** Template for header actions */
  @ContentChild(EMMATextAreaHeaderDirective, { read: TemplateRef, static: false })
  headerTemplate!: TemplateRef<unknown>;
  /** Template for footer actions */
  @ContentChild(EMMATextAreaFooterDirective, { read: TemplateRef, static: false })
  footerTemplate!: TemplateRef<unknown>;
  /** Template to highlight code */
  @ContentChild(EMMATextAreaHighlightDirective, { read: TemplateRef, static: false })
  highlightTemplate!: TemplateRef<unknown>;
  /** Latest requestAnimationFrame identifier for 'highlight' layer syncing */
  private highlightReqAnimation = 0;
  @HostBinding('class') override containerClass = 'form-group m-form__group m-form__group--md';

  // Label
  @Input() first = false;
  @Input() label = '';
  @Input() helpText = '';
  @Input() helpLink = '';
  @Input() helpLinkLabel = $localize`Saber más`;

  // Autocomplete
  @Input() autocompleteItems: Array<SelectOption> = [];
  @Input() autocompleteButtonLabel = '';
  @Input() autocompleteButtonIcon = '';
  @Input() autocompleteNotFound = '';
  @Input() autocompleteGroupBy?: string;
  @Input() autocompleteCheckItems = false;
  @Input() autocompleteCheckItemsModalType = AUTOCOMPLETE_CHECK_TYPE.AUDIENCE_TAGS;
  @Input() autocompleteCheckIcon = '';
  @Input() autocompleteCheckPopoverMsg = '';
  /** Chararcter count */
  @Input() showCharCounter = false;
  @Input() charCounterMaxLength = Infinity;
  @Input() charCounterLength?: number;
  @Input() useMonospaceFont = false;
  @Input() linesToShow = 3;

  // Validation
  @Input() maxlength = Infinity;
  @Input() minlength = 0;

  // Form
  @Input()
  get value(): string {
    return this._innerValue;
  }
  set value(v: string) {
    if (v !== this._innerValue) {
      this._innerValue = v;
      this.onChangeCallback(v);
      this.$change.emit(v);
    }
  }
  private _innerValue = '';
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: string) => void = noop;

  showCheckModal = false;

  override ngOnInit(): void {
    this.updateClasses();
    this.$change
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((ev) => this.onChangeCallback(this._innerValue));
  }

  ngAfterViewInit(): void {
    if (this.highlightTemplate) {
      this.textareaEl.nativeElement.addEventListener('scroll', () => {
        cancelAnimationFrame(this.highlightReqAnimation);
        this.highlightReqAnimation = requestAnimationFrame(() => {
          this.highlightEl.nativeElement.style.top = `-${this.textareaEl.nativeElement.scrollTop}px`;
        });
      });
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if ('containerClass' in changes || 'first' in changes) {
      this.updateClasses();
    }
    if ('autocompleteItems' in changes) {
      this.$change.emit(this._innerValue);
    }
  }
  override updateClasses() {
    this._containerClass = ['emma-textarea', this.containerClass, this.first ? 'm-form__group--first' : ''];
  }

  getCharCounterLength(): number {
    return this.charCounterLength ?? this.value?.length ?? 0;
  }

  onSelectAutocompleteTag(tag: string) {
    this.insertAtCaret(tag || '');
    this.$change.emit(this._innerValue);
  }

  onBlur() {
    this.onTouchedCallback();
    this.$blur.emit();
  }

  private insertAtCaret(text: string) {
    const { nativeElement } = this.textareaEl;
    if (nativeElement) {
      if (isNil(this._innerValue)) {
        this._innerValue = '';
      }
      const ieDocument: any = document;
      if (ieDocument.selection) {
        // IE
        nativeElement.focus();
        const sel = ieDocument.selection.createRange();
        sel.text = text;
        this._innerValue = nativeElement.value;
      } else if (shouldBeNumber(nativeElement.selectionStart)) {
        // Others
        const startPos = Number(nativeElement.selectionStart);
        const endPos = Number(nativeElement.selectionEnd);
        // Elimina el espacio al final si es una substitución
        if (startPos !== endPos) {
          text = text.trim();
        }
        this._innerValue =
          this._innerValue.substring(0, startPos) +
          text +
          this._innerValue.substring(endPos, this._innerValue.length);
        // Para que ocurra después de que Angular reasigne el contenido
        window.requestAnimationFrame(() => {
          nativeElement.selectionStart = startPos + text.length;
          nativeElement.selectionEnd = startPos + text.length;
        });
      } else {
        this._innerValue += text;
      }
      nativeElement.focus();
    }
  }

  // From ControlValueAccessor interface
  writeValue(value: string) {
    if (value !== this._innerValue) {
      this._innerValue = value;
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];

    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.maxlength < Infinity) {
      validators.push(Validators.maxLength(this.maxlength));
    }
    if (this.minlength) {
      validators.push(Validators.minLength(this.minlength));
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };

  onCheckItems(): void {
    this.showCheckModal = true;
  }

  onCancelCheckItems(): void {
    this.showCheckModal = false;
  }
}
